import { EncounterService } from '@quromedical/fhir-common';
import type {
  AdmissionCreateRequest,
  AdmissionUpdateResponse,
} from '@quromedical/models/admission';
import { first } from '@quromedical/utils';
import '@quromedical/utils';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import {
  type AssertRule,
  type AssertRules,
  compose,
  resolveConstant,
} from '../composer';

type AdmissionRuleName =
  | 'new-admission'
  | 'pause-admission'
  | 'resume-admission'
  | 'discharge-admission'
  | 'invalidate-admission'
  | 'start-planned-admission';

type AdmissionParams = {
  admissionType: AdmissionRuleName;
  /**
   * An active admission is admission either in progress, paused or one that is planned.
   * If a patients has no active admission this will be an empty array.
   */
  activeAdmissions: AdmissionUpdateResponse[];
  newAdmission?: AdmissionCreateRequest | AdmissionUpdateResponse;
};

type AssertEncounterServiceRule = AssertRule<AdmissionParams>;

const isOlderThanTwoDays = (startDateString: string | undefined): boolean => {
  if (!startDateString) return false;

  const startDate = parseISO(startDateString);
  if (!isValid(startDate)) return false;

  const currentDate = new Date();
  const timeDifference = differenceInCalendarDays(currentDate, startDate);

  return timeDifference >= 2;
};

// TODO: update to also check that we dont create duplicate planned admissions
const assertCanCreateNewAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
  newAdmission,
}) => {
  if (activeAdmissions.length === 0) {
    return undefined;
  }

  const hasAdmissionInProgress = activeAdmissions.some(
    (a) => a.status === 'in-progress',
  );

  if (hasAdmissionInProgress) {
    return {
      type: 'error',
      messages: [
        'Cannot create a new admission because there is an active admissions',
      ],
    };
  }

  const hasAdmissionPlanned = activeAdmissions.some(
    (a) => a.status === 'planned',
  );

  if (hasAdmissionPlanned) {
    return {
      type: 'error',
      messages: [
        'Cannot create a new admission because there is a planned admission',
      ],
    };
  }

  const isCurrentAdmissionsInPaused = activeAdmissions.filter(
    (a) => a.status === 'onleave',
  );

  const isAdmissionsTypeOfServiceValid = isCurrentAdmissionsInPaused.every(
    (a) => a.service !== newAdmission?.service,
  );

  if (isAdmissionsTypeOfServiceValid) {
    return undefined;
  }

  const findServiceType = Object.values(EncounterService).indexOf(
    newAdmission?.service as unknown as EncounterService,
  );
  const serviceType = Object.keys(EncounterService)[findServiceType];

  return {
    type: 'error',
    messages: [
      `Admission cannot have the same service type of ${serviceType} as the previous paused admissions`,
    ],
  };
};

const assertCanPauseAnAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
}) => {
  const isCurrentAdmissionsInProgress = activeAdmissions.filter(
    (a) => a.status === 'in-progress' || a.status === 'onleave',
  );

  if (isCurrentAdmissionsInProgress.length > 1) {
    return {
      type: 'error',
      messages: [`Only one admission can be paused at a time`],
    };
  }

  const isCurrentAdmissionStatusValid = isCurrentAdmissionsInProgress.find(
    (a) => a.status === 'onleave',
  );

  if (!isCurrentAdmissionStatusValid) {
    return undefined;
  }

  if (isCurrentAdmissionStatusValid) {
    return {
      type: 'error',
      messages: [`Admission cannot be paused because it is in the wrong state`],
    };
  }
};

const assertCanResumeAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
}) => {
  const isCurrentAdmissionsInProgress = activeAdmissions.filter(
    (a) => a.status === 'in-progress' || a.status === 'onleave',
  );

  if (isCurrentAdmissionsInProgress.length > 2) {
    return {
      type: 'error',
      messages: [`Only one admission can be resume at a time`],
    };
  }

  const isCurrentAdmissionStatusValid = isCurrentAdmissionsInProgress.find(
    (a) => a.status === 'in-progress',
  );

  if (!isCurrentAdmissionStatusValid) {
    return undefined;
  }

  if (isCurrentAdmissionStatusValid) {
    return {
      type: 'error',
      messages: ['Patient already has an active admission that is in progress'],
    };
  }
};

const assertCanDischargeAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
}) => {
  const isCurrentAdmissionStatusValid = activeAdmissions.filter(
    (a) => a.status === 'in-progress',
  );

  if (isCurrentAdmissionStatusValid.length <= 0) {
    return {
      type: 'error',
      messages: [
        'Admission cannot be discharged because it is not in-progress',
      ],
    };
  }

  if (isCurrentAdmissionStatusValid.length > 1) {
    return {
      type: 'error',
      messages: ['Cannot discharge more than one admission at a time'],
    };
  }

  const hasStatusHistory = isCurrentAdmissionStatusValid
    .map((history) => history.statusHistory)
    .flat();

  if (hasStatusHistory.length > 0) {
    const isValidStatusHistory = hasStatusHistory.find(
      (a) => a?.status === 'in-progress' && a.period.end === undefined,
    );

    if (
      isValidStatusHistory?.status !== 'in-progress' &&
      isValidStatusHistory?.period.end === undefined
    ) {
      return {
        type: 'error',
        messages: [
          `Admission cannot be discharged because the statusHistory is in the wrong state`,
        ],
      };
    }

    if (isCurrentAdmissionStatusValid) {
      return undefined;
    }
  }
};

const assertCanInvalidateAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
}) => {
  const isCurrentAdmissionStatusValid = activeAdmissions.filter(
    (a) => a.status === 'in-progress' || a.status === 'planned',
  );

  if (isCurrentAdmissionStatusValid.length === 0) {
    return {
      type: 'error',
      messages: [
        'Admission cannot be invalidated because there are no in-progress or planned admissions',
      ],
    };
  }

  if (isCurrentAdmissionStatusValid.length > 1) {
    return {
      type: 'error',
      messages: ['Cannot invalidate more than one admission at a time'],
    };
  }

  const admission = first(isCurrentAdmissionStatusValid);

  if (admission?.status === 'planned') {
    return undefined;
  }

  const olderThanTwoDays = isOlderThanTwoDays(admission?.period?.start);

  if (olderThanTwoDays) {
    return {
      type: 'error',
      messages: [
        'Admission cannot be invalidated because it was created more than 2 days ago',
      ],
    };
  }

  const hasStatusHistory = isCurrentAdmissionStatusValid
    .map((history) => history.statusHistory)
    .flat();

  if (hasStatusHistory.length > 0) {
    const isValidStatusHistory = hasStatusHistory.find(
      (a) => a?.status === 'in-progress' && a.period.end === undefined,
    );

    if (
      isValidStatusHistory?.status !== 'in-progress' &&
      isValidStatusHistory?.period.end === undefined
    ) {
      return {
        type: 'error',
        messages: [
          'Admission cannot be invalidated because the statusHistory is in the wrong state',
        ],
      };
    }

    if (isCurrentAdmissionStatusValid) {
      return undefined;
    }
  }
};

const assertCanStartPlannedAdmission: AssertEncounterServiceRule = ({
  activeAdmissions,
  newAdmission: plannedAdmission,
}) => {
  if (!plannedAdmission) {
    return {
      type: 'error',
      messages: ['No planned admission provided to start'],
    };
  }

  // Check if there's ANY in-progress admission (service type doesn't matter)
  const hasInProgressAdmission = activeAdmissions.some(
    (a) => a.status === 'in-progress',
  );

  if (hasInProgressAdmission) {
    return {
      type: 'error',
      messages: [
        'Cannot start planned admission because there is already an admission in progress',
      ],
    };
  }

  // Check for paused (onleave) admissions of the same service type
  const conflictingPausedAdmission = activeAdmissions.find(
    (a) => a.status === 'onleave' && a.service === plannedAdmission.service,
  );

  if (conflictingPausedAdmission) {
    const findServiceType = Object.values(EncounterService).indexOf(
      plannedAdmission.service as unknown as EncounterService,
    );
    const serviceType = Object.keys(EncounterService)[findServiceType];

    return {
      type: 'error',
      messages: [
        `Cannot start planned admission because there's a paused admission for service type ${serviceType}`,
      ],
    };
  }

  // If all checks pass
  return undefined;
};
const AdmissionAssertions: AssertRules<AdmissionRuleName, AdmissionParams> = {
  'new-admission': [assertCanCreateNewAdmission],
  'pause-admission': [assertCanPauseAnAdmission],
  'resume-admission': [assertCanResumeAdmission],
  'discharge-admission': [assertCanDischargeAdmission],
  'invalidate-admission': [assertCanInvalidateAdmission],
  'start-planned-admission': [assertCanStartPlannedAdmission],
};

/**
 * a function that takes in a set of parameters and based on that determines if a new admission can be created
 */
export const validateAdmissionRequest = compose({
  getRuleName: ({ admissionType }) => admissionType,
  assertions: AdmissionAssertions,
  defaultAssertions: [],
  defaultResolver: resolveConstant<true>(true),
});
